import { IPaginatedResult } from '@/gql/graphql';
import { Nullish } from '@payaca/utilities/types';

type PaginatedGetter<T> = (page: T) => Nullish<IPaginatedResult>;

/**
 * Helper to generate a `getPreviousPageParam` function for TanStack Query infinite queries.
 *
 * For TypeScript to infer types correctly, extract `queryFn` as a separate const before
 * passing it to `useInfiniteQuery`. If you cannot extract `queryFn`, explicitly pass the
 * query data type via generics: `getPreviousPageParamFromPaginatedResult<MyQueryType>(...)`.
 *
 * @example
 * ```ts
 * const queryFn = ({ pageParam }) => gqlClient.request(MyQuery, { offset: pageParam });
 *
 * useInfiniteQuery({
 *   queryFn,
 *   getPreviousPageParam: getPreviousPageParamFromPaginatedResult(
 *     (firstPage) => firstPage.data.items
 *   ),
 * });
 * ```
 */
export function getPreviousPageParamFromPaginatedResult<TQueryData>(
  getter: PaginatedGetter<TQueryData>
): (
  firstPage: TQueryData,
  allPages: TQueryData[],
  firstPageParam: number,
  allPageParams: number[]
) => number | null | undefined {
  return (firstPage) => {
    const paginatedResult = getter(firstPage);

    if (!paginatedResult) {
      return null;
    }

    const newOffset = paginatedResult.offset - paginatedResult.limit;

    if (newOffset < 0) {
      return null;
    }

    return newOffset;
  };
}

/**
 * Helper to generate a `getNextPageParam` function for TanStack Query infinite queries.
 *
 * For TypeScript to infer types correctly, extract `queryFn` as a separate const before
 * passing it to `useInfiniteQuery`. If you cannot extract `queryFn`, explicitly pass the
 * query data type via generics: `getNextPageParamFromPaginatedResult<MyQueryType>(...)`.
 *
 * @example
 * ```ts
 * const queryFn = ({ pageParam }) => gqlClient.request(MyQuery, { offset: pageParam });
 *
 * useInfiniteQuery({
 *   queryFn,
 *   getNextPageParam: getNextPageParamFromPaginatedResult(
 *     (lastPage) => lastPage.data.items
 *   ),
 * });
 * ```
 */
export function getNextPageParamFromPaginatedResult<TQueryData>(
  getter: PaginatedGetter<TQueryData>
): (
  lastPage: TQueryData,
  allPages: TQueryData[],
  lastPageParam: number,
  allPageParams: number[]
) => number | null | undefined {
  return (lastPage) => {
    const paginatedResult = getter(lastPage);

    if (!paginatedResult) {
      return null;
    }

    const newOffset = paginatedResult.offset + paginatedResult.limit;

    if (paginatedResult.totalCount <= newOffset) {
      return null;
    }

    return newOffset;
  };
}
